// One typed endpoint map, mirroring the local frontend's single `api` object.
import { del, get, patch, post } from "./client";
import type {
  AdminStats,
  AdminUser,
  Command,
  DataBackupInfo,
  FleetInfo,
  FleetMember,
  FlowHistory,
  HealthInfo,
  HistoryDailyRecord,
  HistoryDayRecord,
  Me,
  OrgInvite,
  OrgRole,
  MonthlySummary,
  PlanId,
  PendingInvite,
  SettingsBackupInfo,
  ShareLevel,
  SiteCard,
  SiteEvent,
  SiteSharing,
  SubscriptionInfo,
  BillingCycle,
  CheckoutQuote,
} from "./types";

export interface RegisterPending {
  ok: boolean;
  status: "verification_sent";
  verification_required: true;
  email: string;
}

export function isRegisterPending(r: Me | RegisterPending): r is RegisterPending {
  return (r as RegisterPending).status === "verification_sent";
}

export const api = {
  // ----- auth -----
  /** Signed in at once (a `Me`) where e-mail verification is off; otherwise
   *  NO session: `verification_sent` — the same answer for a new and an
   *  already-registered address (no account enumeration). */
  register: (body: { email: string; password: string; name?: string; captcha_token?: string }) =>
    post<Me | RegisterPending>("/api/auth/register", body),
  login: (body: { email: string; password: string; captcha_token?: string }) =>
    post<Me>("/api/auth/login", body),
  verifyEmail: (token: string) =>
    post<{ ok: boolean; email: string }>("/api/auth/verify-email", { token }),
  resendVerification: () =>
    post<{ ok: boolean; already_verified: boolean }>("/api/auth/resend-verification"),
  logout: () => post<{ ok: boolean }>("/api/auth/logout"),
  me: () => get<Me>("/api/auth/me"),
  patchMe: (body: {
    name?: string; lang?: string; password?: string; current_password?: string;
    notify_incidents?: boolean; notify_billing?: boolean; notify_daily_report?: boolean;
  }) =>
    patch<Me>("/api/auth/me", body),
  forgot: (email: string) => post<{ ok: boolean }>("/api/auth/forgot", { email }),

  // ----- sites -----
  sites: () => get<SiteCard[]>("/api/sites"),
  createSite: (body: { name: string; timezone?: string; location?: string }) =>
    post<{ id: number; name: string }>("/api/sites", body),
  patchSite: (id: number, body: { name?: string; timezone?: string; location?: string }) =>
    patch<{ ok: boolean }>(`/api/sites/${id}`, body),
  deleteSite: (id: number) => del<{ ok: boolean }>(`/api/sites/${id}`),
  /** Owner: kill a device's cloud token (lost/sold hardware). */
  revokeSiteDevice: (siteId: number, deviceId: number) =>
    post<{ ok: boolean }>(`/api/sites/${siteId}/devices/${deviceId}/revoke`),
  /** Owner: remove a device and everything stored for it. */
  deleteSiteDevice: (siteId: number, deviceId: number) =>
    del<{ ok: boolean }>(`/api/sites/${siteId}/devices/${deviceId}`),
  claimCode: (siteId: number) =>
    post<{ code: string; expires_at: number }>(`/api/sites/${siteId}/claim-codes`),
  // ----- sharing -----
  createShare: (siteId: number, email: string, role: ShareLevel) =>
    post<{ ok: boolean }>(`/api/sites/${siteId}/shares`, { email, role }),
  siteShares: (siteId: number) => get<SiteSharing>(`/api/sites/${siteId}/shares`),
  cancelInvite: (siteId: number, inviteId: number) =>
    del<{ ok: boolean }>(`/api/sites/${siteId}/invites/${inviteId}`),
  patchSiteMember: (siteId: number, userId: number, role: ShareLevel) =>
    patch<{ ok: boolean }>(`/api/sites/${siteId}/members/${userId}`, { role }),
  removeSiteMember: (siteId: number, userId: number) =>
    del<{ ok: boolean }>(`/api/sites/${siteId}/members/${userId}`),
  myInvites: () => get<PendingInvite[]>("/api/me/invites"),
  acceptInvite: (inviteId: number) =>
    post<{ ok: boolean; site_id: number; role: ShareLevel }>(`/api/me/invites/${inviteId}/accept`),
  declineInvite: (inviteId: number) =>
    post<{ ok: boolean }>(`/api/me/invites/${inviteId}/decline`),
  siteState: (siteId: number) =>
    get<{ site_id: number; devices: SiteCard["devices"] }>(`/api/sites/${siteId}/state`),
  /** `date` is the SITE-local calendar day (YYYY-MM-DD) — always pass it:
   *  the server's default is its own (UTC) today. */
  siteHistoryDay: (siteId: number, date?: string) =>
    get<{ range: "day"; date: string; interval_s: number; records: HistoryDayRecord[] }>(
      `/api/sites/${siteId}/history?range=day${date ? `&date=${date}` : ""}`,
    ),
  siteHistoryDaily: (siteId: number, range: "week" | "month", date?: string) =>
    get<{ range: string; from: string; to: string; records: HistoryDailyRecord[] }>(
      `/api/sites/${siteId}/history?range=${range}${date ? `&date=${date}` : ""}`,
    ),
  siteFlowHistory: (siteId: number) =>
    get<FlowHistory>(`/api/sites/${siteId}/flow-history`),
  siteHealth: (siteId: number) => get<HealthInfo>(`/api/sites/${siteId}/health`),
  siteEvents: (siteId: number) => get<SiteEvent[]>(`/api/sites/${siteId}/events`),
  ackEvent: (siteId: number, eventId: number) =>
    post<{ ok: boolean }>(`/api/sites/${siteId}/events/${eventId}/ack`),
  exportUrl: (siteId: number, scope: "5m" | "daily", format: "csv" | "json") =>
    `/api/sites/${siteId}/export?scope=${scope}&format=${format}`,

  // ----- backups -----
  settingsBackups: (siteId: number) =>
    get<SettingsBackupInfo[]>(`/api/sites/${siteId}/settings-backups`),
  settingsBackupUrl: (siteId: number, deviceId: number, version: number) =>
    `/api/sites/${siteId}/settings-backups/${deviceId}/${version}`,
  restoreSettings: (siteId: number, deviceId: number, version: number) =>
    post<{ ok: boolean; command_id: number }>(
      `/api/sites/${siteId}/settings-backups/${deviceId}/${version}/restore`,
    ),
  dataBackups: (siteId: number) => get<DataBackupInfo[]>(`/api/sites/${siteId}/data-backups`),
  dataBackupUrl: (siteId: number, backupId: number) =>
    `/api/sites/${siteId}/data-backups/${backupId}/download`,

  // ----- commands (remote admin) -----
  commands: (siteId: number) => get<Command[]>(`/api/sites/${siteId}/commands`),
  queueCommand: (siteId: number, type: string, payload: Record<string, unknown>) =>
    post<{ id: number; status: string }>(`/api/sites/${siteId}/commands`, { type, payload }),
  cancelCommand: (siteId: number, commandId: number) =>
    del<{ ok: boolean }>(`/api/sites/${siteId}/commands/${commandId}`),

  // ----- analytics (pro) -----
  siteAnalysis: (siteId: number, month: string) =>
    get<{ site_id: number; device_id: number; summary: MonthlySummary; days: HistoryDailyRecord[] }>(
      `/api/sites/${siteId}/analysis?month=${month}`,
    ),

  // ----- subscription -----
  subscription: () => get<SubscriptionInfo>("/api/subscription"),
  checkout: (body: { plan_id: PlanId; cycle?: BillingCycle }) =>
    post<{
      status: string;
      plan_id: PlanId;
      cycle?: BillingCycle;
      amount_cents?: number;
      vat_cents?: number;
      total_cents?: number;
      effective_at?: number;
    }>("/api/subscription/checkout", body),
  previewCheckout: (plan_id: PlanId, cycle: BillingCycle) =>
    get<CheckoutQuote>(`/api/subscription/preview?plan_id=${plan_id}&cycle=${cycle}`),
  updateBilling: (body: { vat_id?: string; country?: string }) =>
    patch<{ vat_id: string | null; billing_country: string | null }>(
      "/api/subscription/billing",
      body,
    ),
  cancelSubscription: () => post<{ status: string }>("/api/subscription/cancel"),

  // ----- fleet -----
  fleet: () => get<FleetInfo>("/api/fleet"),
  createFleet: (name: string) => post<{ id: number; name: string }>("/api/fleet", { name }),
  attachSite: (siteId: number) => post<{ ok: boolean }>(`/api/fleet/sites/${siteId}`),
  detachSite: (siteId: number) => del<{ ok: boolean }>(`/api/fleet/sites/${siteId}`),
  fleetMembers: () => get<FleetMember[]>("/api/fleet/members"),
  /** Legacy direct add (servers before consent-based membership). */
  addMember: (email: string, role: string) =>
    post<{ ok: boolean }>("/api/fleet/members", { email, role }),
  patchMember: (userId: number, role: string) =>
    patch<{ ok: boolean }>(`/api/fleet/members/${userId}`, { role }),
  removeMember: (userId: number) => del<{ ok: boolean }>(`/api/fleet/members/${userId}`),
  // Consent-based membership: an org admin invites, the invitee accepts on
  // their own account, and any member may leave.
  inviteMember: (email: string, role: OrgRole) =>
    post<{ ok: boolean; id?: number }>("/api/fleet/invites", { email, role }),
  /** Pending invitations the org has sent (org admin view). */
  fleetInvites: () => get<OrgInvite[]>("/api/fleet/invites"),
  cancelFleetInvite: (inviteId: number) => del<{ ok: boolean }>(`/api/fleet/invites/${inviteId}`),
  /** Org invitations addressed to the signed-in account. */
  myOrgInvites: () => get<OrgInvite[]>("/api/me/org-invites"),
  acceptOrgInvite: (inviteId: number) =>
    post<{ ok: boolean }>(`/api/fleet/invites/${inviteId}/accept`),
  declineOrgInvite: (inviteId: number) =>
    post<{ ok: boolean }>(`/api/fleet/invites/${inviteId}/decline`),
  leaveFleet: () => del<{ ok: boolean }>("/api/fleet/members/me"),
  fleetReport: (month: string) =>
    get<{ month: string; sites: (MonthlySummary & { site_id: number; site_name: string })[]; totals: Record<string, number> }>(
      `/api/fleet/report?month=${month}`,
    ),
  fleetDiagnostics: () =>
    get<
      {
        site_id: number;
        site_name: string;
        device_id: number;
        serial: string;
        firmware: string;
        online: boolean;
        last_seen_at: number | null;
        errors: string[];
        wifi_rssi: number | null;
        control_mode: string | null;
      }[]
    >("/api/fleet/diagnostics"),

  // ----- admin -----
  adminStats: () => get<AdminStats>("/api/admin/stats"),
  adminUsers: () => get<AdminUser[]>("/api/admin/users"),
  adminPatchUser: (userId: number, body: { plan_id?: PlanId; is_admin?: boolean; password?: string }) =>
    patch<{ ok: boolean }>(`/api/admin/users/${userId}`, body),
  adminDevices: () =>
    get<
      {
        id: number;
        site_id: number;
        site_name: string;
        owner_user_id: number;
        serial: string;
        model: string;
        firmware: string;
        online: boolean;
        last_seen_at: number | null;
        registered_at: number;
        revoked: boolean;
      }[]
    >("/api/admin/devices"),
  adminRevokeDevice: (deviceId: number) =>
    post<{ ok: boolean }>(`/api/admin/devices/${deviceId}/revoke`),
  adminMetrics: () =>
    get<{
      events: { event: string; count: number; sessions: number }[];
      plans: { plan: string | null; count: number }[];
      calculator_conversion: {
        sessions_total: number;
        sessions_calculator: number;
        sessions_registered: number;
        conversion_with_calculator_pct: number | null;
        conversion_without_calculator_pct: number | null;
      };
    }>("/api/admin/metrics"),
};
